import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from './supabase';

// ── Helpers ──────────────────────────────────────────────

function getSessionId() {
  let sid = sessionStorage.getItem('ibrc_sid');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('ibrc_sid', sid);
  }
  return sid;
}

function getVisitorId() {
  let vid = localStorage.getItem('ibrc_vid');
  if (!vid) {
    vid = crypto.randomUUID();
    localStorage.setItem('ibrc_vid', vid);
  }
  return vid;
}

function getDeviceType() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
}

function getOS() {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
}

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const utms = {};
  let hasAny = false;

  keys.forEach((k) => {
    const v = params.get(k);
    if (v) {
      utms[k] = v;
      hasAny = true;
    }
  });

  // Persist in sessionStorage (first touch wins within session)
  if (hasAny) {
    sessionStorage.setItem('ibrc_utm', JSON.stringify(utms));
  }

  return JSON.parse(sessionStorage.getItem('ibrc_utm') || '{}');
}

// ── New visitor / session page counter ───────────────────

function isNewVisitor() {
  const key = 'ibrc_first_visit';
  if (!localStorage.getItem(key)) {
    localStorage.setItem(key, Date.now().toString());
    return true;
  }
  return false;
}

function incrementPagesInSession() {
  const key = 'ibrc_pages_count';
  const count = parseInt(sessionStorage.getItem(key) || '0', 10) + 1;
  sessionStorage.setItem(key, count.toString());
  return count;
}

function getPagesInSession() {
  return parseInt(sessionStorage.getItem('ibrc_pages_count') || '0', 10);
}

// ── Engagement helpers ────────────────────────────────────

const SESSION_START_KEY = 'ibrc_session_start';
const ENGAGED_KEY = 'ibrc_engaged';

function getSessionStart() {
  let ts = sessionStorage.getItem(SESSION_START_KEY);
  if (!ts) {
    ts = Date.now().toString();
    sessionStorage.setItem(SESSION_START_KEY, ts);
  }
  return parseInt(ts, 10);
}

function markEngaged() {
  sessionStorage.setItem(ENGAGED_KEY, '1');
}

function isEngaged() {
  return sessionStorage.getItem(ENGAGED_KEY) === '1';
}

function checkEngagement(pagesCount) {
  if (isEngaged()) return;
  const elapsed = Date.now() - getSessionStart();
  if (elapsed >= 10_000 || pagesCount >= 2) {
    markEngaged();
  }
}

// ── Geo / IP ─────────────────────────────────────────────

const GEO_KEY = 'ibrc_geo';

async function fetchGeoInfo() {
  try {
    const res = await fetch('/api/visitor-info');
    if (!res.ok) return null;
    const data = await res.json();
    sessionStorage.setItem(GEO_KEY, JSON.stringify(data));
    return data;
  } catch (_) {
    return null;
  }
}

function getGeoInfo() {
  const cached = sessionStorage.getItem(GEO_KEY);
  return cached ? JSON.parse(cached) : null;
}

// Kick off geo fetch on first load (fire-and-forget) — only when consent is not rejected
if (typeof window !== 'undefined' && !sessionStorage.getItem(GEO_KEY)) {
  try {
    if (localStorage.getItem('ibrc_cookie_consent') !== 'rejected') {
      fetchGeoInfo();
    }
  } catch (_) {}
}

// ── LGPD consent helper ───────────────────────────────────
// Returns 'accepted' | 'rejected' | null (not yet answered)

function getConsent() {
  try { return localStorage.getItem('ibrc_cookie_consent'); } catch { return null; }
}

// ── Track Page View ──────────────────────────────────────

async function trackPageView(path) {
  const consent = getConsent();

  if (consent === 'rejected') {
    // Anonymous aggregate only — no personal identifiers (LGPD Art. 10)
    await supabase.from('page_views').insert({
      page_path: path || window.location.pathname,
      page_title: document.title,
    });
    return;
  }

  // accepted or not yet answered — full tracking (legitimate interest basis)
  const utms = getUTMParams();

  const row = {
    page_path: path || window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || null,
    utm_source: utms.utm_source || null,
    utm_medium: utms.utm_medium || null,
    utm_campaign: utms.utm_campaign || null,
    utm_term: utms.utm_term || null,
    utm_content: utms.utm_content || null,
    device_type: getDeviceType(),
    browser: getBrowser(),
    os: getOS(),
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    language: navigator.language,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
  };

  if (consent === 'accepted') {
    const geo = getGeoInfo();
    if (geo) {
      row.ip = geo.ip || null;
      row.country = geo.country || null;
      row.region = geo.region || null;
      row.city = geo.city || null;
    }
  }

  await supabase.from('page_views').insert(row);
}

// ── Track Event ──────────────────────────────────────────

async function trackEvent(eventName, opts = {}) {
  const consent = getConsent();

  if (consent === 'rejected') {
    // Anonymous aggregate only — strip all personal identifiers
    await supabase.from('site_events').insert({
      event_name: eventName,
      event_category: opts.category || null,
      page_path: opts.pagePath || window.location.pathname,
      properties: opts.properties || {},
    });
    return;
  }

  // accepted or not yet answered — full tracking
  await supabase.from('site_events').insert({
    event_name: eventName,
    event_category: opts.category || null,
    page_path: opts.pagePath || window.location.pathname,
    element_id: opts.elementId || null,
    element_text: opts.elementText || null,
    properties: opts.properties || {},
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
  });
}

// ── Hook: usePageTracking ────────────────────────────────
// Drop into App.js — auto-tracks every route change

export function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    // Avoid duplicate on same path
    const path = location.pathname + location.hash;
    if (path === lastPath.current) return;
    lastPath.current = path;

    const pagesCount = incrementPagesInSession();
    checkEngagement(pagesCount);
    trackPageView(path);
  }, [location.pathname, location.hash]);
}

// ── Hook: useTrackEvent ──────────────────────────────────
// Returns a stable trackEvent function for components

export function useTrackEvent() {
  return useCallback((eventName, opts) => {
    trackEvent(eventName, opts);
  }, []);
}

// ── Hook: useTrackModal ──────────────────────────────────
// Auto-tracks modal open/close

export function useTrackModal(modalName, isOpen) {
  const prevOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      trackEvent('modal_open', {
        category: 'engagement',
        properties: { modal: modalName },
      });
      // Also count as virtual pageview
      trackPageView(`/modal/${modalName}`);
    }
    if (!isOpen && prevOpen.current) {
      trackEvent('modal_close', {
        category: 'engagement',
        properties: { modal: modalName },
      });
    }
    prevOpen.current = isOpen;
  }, [isOpen, modalName]);
}

// ── Hook: useScrollDepth ─────────────────────────────────
// Tracks max scroll depth milestones (25 / 50 / 75 / 100 %)
// per page. Re-runs on each path change.

export function useScrollDepth() {
  const location = useLocation();
  const firedRef = useRef(new Set());
  const pathRef = useRef(location.pathname);

  useEffect(() => {
    const path = location.pathname;

    // Reset milestones when the page changes
    if (path !== pathRef.current) {
      firedRef.current = new Set();
      pathRef.current = path;
    }

    const thresholds = [25, 50, 75, 100];

    function handleScroll() {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const pct = Math.round((scrolled / total) * 100);

      for (const threshold of thresholds) {
        if (pct >= threshold && !firedRef.current.has(threshold)) {
          firedRef.current.add(threshold);
          trackEvent('scroll_depth', {
            category: 'engagement',
            properties: { depth: threshold, page: path },
          });
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Fire once on mount in case the page is short
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);
}

// ── Hook: useWebVitals ───────────────────────────────────
// Captures LCP, CLS, and FID via PerformanceObserver
// (no external dependency — uses the raw browser APIs)

function rateMetric(name, value) {
  if (name === 'LCP') {
    if (value <= 2500) return 'good';
    if (value <= 4000) return 'needs-improvement';
    return 'poor';
  }
  if (name === 'CLS') {
    if (value <= 0.1) return 'good';
    if (value <= 0.25) return 'needs-improvement';
    return 'poor';
  }
  if (name === 'FID') {
    if (value <= 100) return 'good';
    if (value <= 300) return 'needs-improvement';
    return 'poor';
  }
  return 'good';
}

function sendVital(name, value) {
  trackEvent('web_vital', {
    category: 'performance',
    properties: { metric: name, value: Math.round(value * 100) / 100, rating: rateMetric(name, value) },
  });
}

export function useWebVitals() {
  useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return;

    const observers = [];

    // LCP — Largest Contentful Paint
    try {
      let lastLCP = null;
      const lcpObs = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length) lastLCP = entries[entries.length - 1];
      });
      lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObs);

      // LCP is final only when the user first interacts or page hides
      function finalizeLCP() {
        if (lastLCP) {
          sendVital('LCP', lastLCP.startTime);
          lastLCP = null;
        }
        document.removeEventListener('keydown', finalizeLCP, true);
        document.removeEventListener('click', finalizeLCP, true);
        document.removeEventListener('pointerdown', finalizeLCP, true);
        document.removeEventListener('visibilitychange', finalizeLCP);
      }
      ['keydown', 'click', 'pointerdown'].forEach((e) =>
        document.addEventListener(e, finalizeLCP, { once: true, capture: true })
      );
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') finalizeLCP();
      });
    } catch (_) {}

    // CLS — Cumulative Layout Shift
    try {
      let clsValue = 0;
      let clsSessionValue = 0;
      let clsSessionEntries = [];

      const clsObs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            const firstEntry = clsSessionEntries[0];
            const lastEntry = clsSessionEntries[clsSessionEntries.length - 1];

            if (
              clsSessionEntries.length === 0 ||
              entry.startTime - lastEntry.startTime < 1000 ||
              entry.startTime - firstEntry.startTime < 5000
            ) {
              clsSessionValue += entry.value;
              clsSessionEntries.push(entry);
            } else {
              clsValue = Math.max(clsValue, clsSessionValue);
              clsSessionValue = entry.value;
              clsSessionEntries = [entry];
            }
            clsValue = Math.max(clsValue, clsSessionValue);
          }
        }
      });
      clsObs.observe({ type: 'layout-shift', buffered: true });
      observers.push(clsObs);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          sendVital('CLS', clsValue);
        }
      });
    } catch (_) {}

    // FID — First Input Delay
    try {
      const fidObs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          sendVital('FID', entry.processingStart - entry.startTime);
        }
      });
      fidObs.observe({ type: 'first-input', buffered: true });
      observers.push(fidObs);
    } catch (_) {}

    return () => {
      observers.forEach((obs) => {
        try { obs.disconnect(); } catch (_) {}
      });
    };
  }, []); // mount once per app session
}

// ── Hook: useEngagement ──────────────────────────────────
// Sends page_exit and session_summary events on unload /
// visibility-hidden. Also fires the 10-second engagement mark.

export function useEngagement() {
  const location = useLocation();
  const pageStartRef = useRef(Date.now());
  const pathRef = useRef(location.pathname);
  const engagementTimerRef = useRef(null);

  useEffect(() => {
    // Initialise session start timestamp (idempotent)
    getSessionStart();

    // 10-second engagement timer
    if (!isEngaged()) {
      engagementTimerRef.current = setTimeout(() => {
        markEngaged();
      }, 10_000);
    }

    return () => {
      if (engagementTimerRef.current) clearTimeout(engagementTimerRef.current);
    };
  }, []); // once on mount

  useEffect(() => {
    // Reset page timer on route change
    pageStartRef.current = Date.now();
    pathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    function sendPageExit() {
      const duration_ms = Date.now() - pageStartRef.current;
      trackEvent('page_exit', {
        category: 'engagement',
        properties: { duration_ms, page: pathRef.current },
      });
    }

    function sendSessionSummary() {
      trackEvent('session_summary', {
        category: 'session',
        properties: {
          pages_count: getPagesInSession(),
          duration_total_ms: Date.now() - getSessionStart(),
          is_engaged: isEngaged(),
          is_new_visitor: localStorage.getItem('ibrc_first_visit')
            ? localStorage.getItem('ibrc_first_visit') === Date.now().toString()
            : false,
        },
      });
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        sendPageExit();
        sendSessionSummary();
      }
    }

    function handleBeforeUnload() {
      sendPageExit();
      sendSessionSummary();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // attach once; refs hold current values
}

// ── track404 ─────────────────────────────────────────────
// Call on your 404 / Not Found page component.

export async function track404(missingPath) {
  await trackEvent('error_404', {
    category: 'error',
    properties: {
      path: missingPath || window.location.pathname,
      referrer: document.referrer || null,
    },
  });
}

// ── trackArticleView ─────────────────────────────────────
// Call when an article is opened inside a modal/detail view.
// article = { slug, title, author, category, id }

export async function trackArticleView(article) {
  await trackPageView(`/artigos/${article.slug}`);
  await trackEvent('article_view', {
    category: 'content',
    properties: {
      slug: article.slug,
      title: article.title,
      author: article.author,
      category: article.category,
      article_id: article.id,
      source: 'modal',
    },
  });
}

// ── Standalone exports ───────────────────────────────────

export { trackPageView, trackEvent, getUTMParams, getGeoInfo };
